import type {
  HelpdeskTicketScope,
  HelpdeskTicketStatusFilter,
  IHelpdeskConfig,
  IHelpdeskCreateTicketRequest,
  IHelpdeskDownloadUrl,
  IHelpdeskMessage,
  IHelpdeskMessageCreated,
  IHelpdeskSendMessageRequest,
  IHelpdeskSurveyRequest,
  IHelpdeskTicketDetail,
  IHelpdeskTicketPage,
  IHelpdeskUnreadCount,
  IHelpdeskUploadRequest,
  IHelpdeskUploadRequestResponse
} from "@kuika/kuika-cl-model";
import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosInstance, AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from "axios";

// CoreApi's HelpdeskController: an authorized proxy of the Kuika ITSM external API. The ITSM client
// secret stays on the server; the browser only ever talks to CoreApi and, for file bytes, to the
// short-lived storage URL CoreApi hands back.
const helpdeskUrl: string = `${CommonHelperFunctions.GetBackendUrl()}/Helpdesk`;

// A bare instance for the storage PUT: the global interceptors add the designer's bearer token and
// workspace headers, and none of that may reach the storage account.
const storageClient: AxiosInstance = Axios.create();

function withIdempotencyKey(idempotencyKey: string | undefined): AxiosRequestConfig {
  return idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {};
}

function ticketUrl(ticketNumber: number): string {
  return `${helpdeskUrl}/Tickets/${ticketNumber}`;
}

function attachmentUrl(ticketNumber: number, attachmentId: string): string {
  return `${ticketUrl(ticketNumber)}/Attachments/${encodeURIComponent(attachmentId)}`;
}

export class HelpdeskService {
  public static async getConfig(): Promise<IHelpdeskConfig> {
    const response: AxiosResponse<IHelpdeskConfig> = await Axios.get<IHelpdeskConfig>(`${helpdeskUrl}/Config`);
    return response.data;
  }

  public static async createTicket(
    request: IHelpdeskCreateTicketRequest,
    idempotencyKey: string
  ): Promise<IHelpdeskTicketDetail> {
    const response: AxiosResponse<IHelpdeskTicketDetail> = await Axios.post<IHelpdeskTicketDetail>(
      `${helpdeskUrl}/Tickets`,
      request,
      withIdempotencyKey(idempotencyKey)
    );
    return response.data;
  }

  /** `updatedSince` (ISO 8601): only the tickets changed after it, for an incremental refresh. */
  public static async getTickets(
    scope: HelpdeskTicketScope,
    status: HelpdeskTicketStatusFilter,
    page: number,
    pageSize: number,
    updatedSince?: string
  ): Promise<IHelpdeskTicketPage> {
    const response: AxiosResponse<IHelpdeskTicketPage> = await Axios.get<IHelpdeskTicketPage>(
      `${helpdeskUrl}/Tickets`,
      { params: { scope, status, page, pageSize, updatedSince } }
    );
    return response.data;
  }

  public static async getUnreadCount(scope: HelpdeskTicketScope): Promise<number> {
    const response: AxiosResponse<IHelpdeskUnreadCount> = await Axios.get<IHelpdeskUnreadCount>(
      `${helpdeskUrl}/Tickets/UnreadCount`,
      { params: { scope } }
    );
    return response.data?.count ?? 0;
  }

  public static async getTicket(ticketNumber: number): Promise<IHelpdeskTicketDetail> {
    const response: AxiosResponse<IHelpdeskTicketDetail> = await Axios.get<IHelpdeskTicketDetail>(
      ticketUrl(ticketNumber)
    );
    return response.data;
  }

  public static async markRead(ticketNumber: number): Promise<void> {
    await Axios.post(`${ticketUrl(ticketNumber)}/Read`);
  }

  public static async getMessages(ticketNumber: number): Promise<IHelpdeskMessage[]> {
    const response: AxiosResponse<IHelpdeskMessage[]> = await Axios.get<IHelpdeskMessage[]>(
      `${ticketUrl(ticketNumber)}/Messages`
    );
    return response.data ?? [];
  }

  public static async sendMessage(
    ticketNumber: number,
    request: IHelpdeskSendMessageRequest,
    idempotencyKey: string
  ): Promise<IHelpdeskMessageCreated> {
    const response: AxiosResponse<IHelpdeskMessageCreated> = await Axios.post<IHelpdeskMessageCreated>(
      `${ticketUrl(ticketNumber)}/Messages`,
      request,
      withIdempotencyKey(idempotencyKey)
    );
    return response.data;
  }

  public static async requestUpload(
    ticketNumber: number,
    request: IHelpdeskUploadRequest,
    idempotencyKey: string
  ): Promise<IHelpdeskUploadRequestResponse> {
    const response: AxiosResponse<IHelpdeskUploadRequestResponse> = await Axios.post<IHelpdeskUploadRequestResponse>(
      `${ticketUrl(ticketNumber)}/Attachments/UploadRequests`,
      request,
      withIdempotencyKey(idempotencyKey)
    );
    return response.data;
  }

  /** PUTs the file bytes straight to the storage URL from `requestUpload` (Azure block blob). */
  public static async putFile(uploadUrl: string, file: File, onProgress: (percent: number) => void): Promise<void> {
    await storageClient.put(uploadUrl, file, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type || "application/octet-stream"
      },
      onUploadProgress: (event: AxiosProgressEvent): void => {
        if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
  }

  public static async completeUpload(ticketNumber: number, attachmentId: string): Promise<void> {
    await Axios.post(`${attachmentUrl(ticketNumber, attachmentId)}/Complete`);
  }

  public static async getDownloadUrl(ticketNumber: number, attachmentId: string): Promise<IHelpdeskDownloadUrl> {
    const response: AxiosResponse<IHelpdeskDownloadUrl> = await Axios.get<IHelpdeskDownloadUrl>(
      `${attachmentUrl(ticketNumber, attachmentId)}/DownloadUrl`
    );
    return response.data;
  }

  public static async submitSurvey(
    ticketNumber: number,
    request: IHelpdeskSurveyRequest,
    idempotencyKey: string
  ): Promise<void> {
    await Axios.post(`${ticketUrl(ticketNumber)}/Survey`, request, withIdempotencyKey(idempotencyKey));
  }
}
